import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Download, ChevronUp, ChevronDown } from 'lucide-react';
import { EiaForecastData, dataService, apiClient } from '@rti/shared';
import EiaForecastChart from '../components/EiaForecastChart';
import AIPanel from '../components/AIPanel';
import { trackUserEvent } from "@/utils/userTracking";

interface EiaForecastProps {}

const EiaForecast: React.FC<EiaForecastProps> = () => {
  const navigate = useNavigate();
  const hasFetched = useRef(false);

  const [loading, setLoading] = useState(true);
  const [eiaData, setEiaData] = useState<EiaForecastData[]>([]);
  const [chartLoading, setChartLoading] = useState(true);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  // Detect small screens to tailor table date format
  const [isMobile, setIsMobile] = useState(() => (typeof window !== 'undefined' ? window.matchMedia('(max-width: 640px)').matches : false));
  useEffect(() => {
    trackUserEvent("view_eia_forecast_page", "EIA Forecast Analysis Viewed");
    const mql = typeof window !== 'undefined' ? window.matchMedia('(max-width: 640px)') : null;
    const handler = (e: MediaQueryListEvent | MediaQueryList) => setIsMobile('matches' in e ? e.matches : (e as MediaQueryList).matches);

    if (mql) {
      // Initial sync
      setIsMobile(mql.matches);
      // Modern browsers
      if (typeof mql.addEventListener === 'function') {
        mql.addEventListener('change', handler as EventListener);
      } else if (typeof mql.addListener === 'function') {
        // Safari <14
        mql.addListener(handler as (this: MediaQueryList, ev: MediaQueryListEvent) => any);
      }
    }

    return () => {
      if (mql) {
        if (typeof mql.removeEventListener === 'function') {
          mql.removeEventListener('change', handler as EventListener);
        } else if (typeof mql.removeListener === 'function') {
          // Safari <14
          mql.removeListener(handler as (this: MediaQueryList, ev: MediaQueryListEvent) => any);
        }
      }
    };
  }, []);

  // Display formatter: MM/DD/YYYY on mobile, otherwise Mon D, YYYY
  const formatDisplayDate = useCallback((dateString: string) => {
    // Create date using year, month, day parts to avoid timezone shifts
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    if (isMobile) {
      return date.toLocaleDateString('en-US', {
        year: '2-digit',
        month: '2-digit',
        day: '2-digit'
      });
    }
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }, [isMobile]);

  const fetchEiaData = async () => {
    try {
      setLoading(true);
      let data = await dataService.getEiaForecastData().then(d => d || []);
      data = data.map(row => ({
        ...row,
        brent_crude_oil_spot_price: Number(row.brent_crude_oil_spot_price),
        wti_crude_oil_spot_price: Number(row.wti_crude_oil_spot_price),
        natural_gas_henry_hub_price: Number(row.natural_gas_henry_hub_price),
        natural_gas_henry_hub_price_usd: Number(row.natural_gas_henry_hub_price_usd),
      }));

      if (Array.isArray(data)) {
        setEiaData(data);
      } else {
        console.error('EIA data is not an array:', data);
        setEiaData([]);
      }
    } catch (error) {
      console.error('EIA data fetching error:', error);
      setEiaData([]);
    } finally {
      setLoading(false);
      setTimeout(() => setChartLoading(false), 300);
    }
  };

  const handleBackToDashboard = () => {
    navigate('/dashboard');
  };

  const handleDateSort = () => {
    const newSortOrder = sortOrder === 'asc' ? 'desc' : 'asc';
    setSortOrder(newSortOrder);

    const sortedData = [...eiaData].sort((a, b) => {
      const dateA = new Date(a.date).getTime();
      const dateB = new Date(b.date).getTime();
      return newSortOrder === 'asc' ? dateA - dateB : dateB - dateA;
    });

    setEiaData(sortedData);
  };

  useEffect(() => {
    if (!hasFetched.current) {
      hasFetched.current = true;
      fetchEiaData();
    }
  }, []);

  if (loading) {
    return (
      <div className="text-3xl flex text-rs-off-white items-center justify-center h-[500px] w-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col p-5 w-full dashboard-shadow max-w-8xl mt-5">
      <div className="content-toolbar">
        <button
          className="jet flex items-center gap-2 px-4 py-2 bg-rs-jet text-white rounded"
          onClick={handleBackToDashboard}
        >
          <span className="material-symbols-outlined">arrow_back_ios</span>
          Back to Landing Page
        </button>
      </div>

      <div className="flex justify-between items-center pt-5 pb-3">
        <p className="text-2xl font-light text-rs-off-white">
          EIA Energy Forecast Analysis
        </p>
      </div>

      <hr className="my-3 pb-5 -mx-5 border-rs-jet" />

      <div className="bg-rs-off-black rounded-lg p-5 flex items-center justify-center flex-col mb-10">
        <h4 className="text-xl text-center mb-3 font-medium text-rs-off-white">
          Oil & Natural Gas Price Forecast
        </h4>
        <div className="w-full">
          {chartLoading ? (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-rs-dark-green"></div>
            </div>
          ) : (
            <EiaForecastChart eiaData={eiaData} />
          )}
        </div>
      </div>

      {/* Data Table Section */}
      <AIPanel
        fetchFn={(force) => apiClient.getAIInsights('eia-forecast', force)}
        regenerateFn={(overrides) => apiClient.regenerateAiInsights('eia-forecast', overrides)}
        context="eia-forecast"
      />
      <div className="bg-rs-off-black rounded-lg p-5 mt-5">
        <div className="flex justify-between items-center mb-4 max-[470px]:flex-col max-[470px]:gap-3">
          <h4 className="text-xl font-medium text-rs-off-white max-[470px]:text-center">
            EIA Forecast Data
          </h4>
          <button
            onClick={() => exportToCSV(eiaData)}
            className="flex items-center gap-2 px-4 py-2 bg-rs-green !text-black rounded hover:bg-rs-green/80 transition-colors"
          >
            <Download size={20} />
            Export Data
          </button>
        </div>

        <div className="overflow-x-auto" style={{ transform: 'rotateX(180deg)' }}>
          <table className="w-full text-sm text-rs-off-white" style={{ transform: 'rotateX(180deg)' }}>
            <thead>
              <tr className="border-b border-rs-jet">
                <th
                  className="text-center py-3 px-4 font-medium cursor-pointer hover:bg-rs-jet/20 select-none"
                  onClick={handleDateSort}
                >
                  <div className="flex items-center justify-center gap-2">
                    Date
                    {sortOrder === 'asc' ? (
                      <ChevronUp size={16} className="text-rs-dark-green" />
                    ) : (
                      <ChevronDown size={16} className="text-rs-dark-green" />
                    )}
                  </div>
                </th>
                { !isMobile && (
                  <th className="text-center py-3 px-4 font-medium">Quarter</th>
                )}
                <th className="text-center py-3 px-4 font-medium">Brent Crude Oil ($/bbl)</th>
                <th className="text-center py-3 px-4 font-medium">WTI Crude Oil ($/bbl)</th>
                <th className="text-center py-3 px-4 font-medium">Natural Gas ($/MMBtu)</th>
              </tr>
            </thead>
            <tbody>
              {eiaData.length > 0 ? (
                eiaData.map((row, index) => (
                  <tr key={index} className={`border-b border-rs-jet/50 hover:bg-rs-jet/30 ${index % 2 === 0 ? 'bg-rs-jet-light/20' : ''}`}>
                    <td className="py-3 px-4 text-center">{formatDisplayDate(row.date)}</td>
                    { !isMobile && (
                    <td className="py-3 px-4 text-center">Q{row.quarter}</td>
                    )}
                    <td className="py-3 px-4 text-center">${row.brent_crude_oil_spot_price.toFixed(2)}</td>
                    <td className="py-3 px-4 text-center">${row.wti_crude_oil_spot_price.toFixed(2)}</td>
                    <td className="py-3 px-4 text-center">${row.natural_gas_henry_hub_price_usd.toFixed(3)}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="py-8 px-4 text-center text-rs-off-white/60">
                    No data available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// Helper functions
// Removed old formatDate and rely on in-component formatter for responsive behavior

const exportToCSV = (data: EiaForecastData[]) => {
  if (!data || data.length === 0) {
    alert('No data to export');
    return;
  }

  trackUserEvent("download_eia_forecast_data", "EIA Forecast Data Downloaded");
  const headers = [
    'Date',
    'Quarter',
    'Brent Crude Oil Spot Price ($/bbl)',
    'WTI Crude Oil Spot Price ($/bbl)',
    'Natural Gas Spot Henry Hub ($/MMBtu)'
  ];

  const csvContent = [
    headers.join(','),
    ...data.map(row => [
      `"${row.date}"`,
      row.quarter,
      row.brent_crude_oil_spot_price.toFixed(2),
      row.wti_crude_oil_spot_price.toFixed(2),
      row.natural_gas_henry_hub_price_usd.toFixed(3)
    ].join(','))
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', `eia-forecast-${new Date().toISOString().split('T')[0]}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export default EiaForecast;
